/**
 * Cache Details Panel Component
 * Shows extended cache metrics including storage info and service health status
 */

import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import {
  Database,
  Key,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Trash2,
  Loader2,
} from 'lucide-react';
import { useI18n } from '@/i18n';
import type { CacheMetrics } from '@/types/monitoring';
import { monitoringApi } from '@/lib/api/monitoring';

export interface CacheDetailsPanelProps {
  metrics: CacheMetrics | null;
  onCacheCleared?: () => void;
}

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Format number with thousands separator
 */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function CacheDetailsPanel({ metrics, onCacheCleared }: CacheDetailsPanelProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.cacheDetails;
  const tCache = messages.monitoring.cache;

  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const handleClearCache = async () => {
    if (!confirm(t.clearConfirm)) {
      return;
    }

    setIsClearing(true);
    setClearError(null);

    try {
      const result = await monitoringApi.clearBgeM3Cache();
      if (result.success) {
        alert(t.clearSuccess.replace('{count}', String(result.deleted)));
        onCacheCleared?.();
      } else {
        setClearError(result.error || t.clearError);
      }
    } catch (error) {
      setClearError(error instanceof Error ? error.message : t.clearError);
    } finally {
      setIsClearing(false);
    }
  };

  if (!metrics) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t.title}</h3>
        </div>
        <div className="text-center py-8 text-gray-400">
          <Database className="w-12 h-12 mx-auto mb-2" />
          <p>{tCache.unavailable}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t.title}</h3>
        <button
          onClick={handleClearCache}
          disabled={isClearing}
          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
        >
          {isClearing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
          <span>{t.clear}</span>
        </button>
      </div>

      {clearError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {clearError}
        </div>
      )}

      <div className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Requests */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Database className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">{t.totalRequests}</p>
            </div>
            <p className="text-xl font-bold text-gray-900">
              {formatNumber(metrics.total_requests)}
            </p>
          </div>

          {/* Cached Keys */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Key className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">{t.cachedKeys}</p>
            </div>
            <p className="text-xl font-bold text-gray-900">
              {formatNumber(metrics.cached_keys)}
            </p>
          </div>

          {/* Cache Size */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <HardDrive className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">{t.cacheSize}</p>
            </div>
            <p className="text-xl font-bold text-gray-900">
              {formatBytes(metrics.cache_size_bytes)}
            </p>
          </div>

          {/* Errors */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-gray-400" />
              <p className="text-xs text-gray-500">{t.errors}</p>
            </div>
            <p className={`text-xl font-bold ${metrics.errors > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {formatNumber(metrics.errors)}
            </p>
          </div>
        </div>

        {/* Service Health Status */}
        <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Redis Connection */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{t.redisConnection}</span>
            <span
              className={`flex items-center gap-1 text-sm font-medium ${
                metrics.redis_connected ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {metrics.redis_connected ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              <span>{metrics.redis_connected ? t.connected : t.disconnected}</span>
            </span>
          </div>

          {/* TEI Backend */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{t.teiBackend}</span>
            <span
              className={`flex items-center gap-1 text-sm font-medium ${
                metrics.tei_healthy ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {metrics.tei_healthy ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              <span>{metrics.tei_healthy ? t.healthy : t.unhealthy}</span>
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
